import { SelectComponent } from '@/components/select';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { resetCells, resetDistricts, resetSectors, resetVillages } from '@/store/slices';
import { AppDispatch, RootState } from '@/store/store';
import {
  fetchCellsBySector,
  fetchDistrictsByProvince,
  fetchGenders,
  fetchProvinces,
  fetchSectorsByDistrict,
  fetchVillagesByCell
} from '@/store/thunks';
import {
  ArrowLeft,
  ChevronDown,
  FileBadge,
  Loader2,
  MapPinned,
  Phone,
  UserRound
} from 'lucide-react';
import { ComponentType, ReactNode, useEffect, useId, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NewVoterFormValues } from '../schema';
import { DatePicker } from '@/components/datepicker';

interface NewVoterFormProps {
  isLoading: boolean;
  onSubmit: (data: NewVoterFormValues) => void;
  onCancel: () => void;
}

interface CardSectionProps {
  title: string;
  description?: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}

const Asterisk = () => <span className="text-red-500">*</span>;

const CardSection = ({ title, description, icon: Icon, children }: CardSectionProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const contentId = useId();

  return (
    <section className="overflow-hidden rounded-md border border-[#e2e9f3] bg-white shadow-[0_10px_30px_rgba(26,53,91,0.045)]">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-4 px-5 py-5 text-left transition-colors hover:bg-[#f8faff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2474d2]/30 sm:px-6"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#eaf3ff] text-[#2374cf]">
          <Icon className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-[#172641] sm:text-lg">{title}</span>
          {description && (
            <span className="mt-1 block text-xs leading-5 text-[#71809a] sm:text-sm">
              {description}
            </span>
          )}
        </span>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#dfe7f2] bg-white text-[#66758e]">
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      <div id={contentId} hidden={!isOpen} className="border-t border-[#edf1f6] px-5 py-6 sm:px-6">
        {children}
      </div>
    </section>
  );
};

export function NewVoterForm({ isLoading, onSubmit, onCancel }: NewVoterFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const form = useFormContext<NewVoterFormValues>();
  const { watch, setValue } = form;

  const provinceId = watch('province');
  const districtId = watch('district');
  const sectorId = watch('sector');
  const cellId = watch('cell');
  const villageId = watch('village');

  // Redux selectors
  const { items: provinces } = useSelector((state: RootState) => state.provinces);
  const { districtsByProvince } = useSelector((state: RootState) => state.districts);
  const { sectorsByDistrict } = useSelector((state: RootState) => state.sectors);
  const { cellsBySector } = useSelector((state: RootState) => state.cells);
  const { villagesByCell } = useSelector((state: RootState) => state.villages);
  const { items: genders } = useSelector((state: RootState) => state.genders);

  // Load initial data
  useEffect(() => {
    dispatch(fetchProvinces({ page: 1, limit: 100 }));
    dispatch(fetchGenders({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Location cascade effects
  useEffect(() => {
    if (provinceId) {
      dispatch(fetchDistrictsByProvince(provinceId));
    } else {
      dispatch(resetDistricts());
      setValue('district', '');
    }
  }, [provinceId, dispatch, setValue]);

  useEffect(() => {
    if (districtId) {
      dispatch(fetchSectorsByDistrict(districtId));
    } else {
      dispatch(resetSectors());
      setValue('sector', '');
    }
  }, [districtId, dispatch, setValue]);

  useEffect(() => {
    if (sectorId) {
      dispatch(fetchCellsBySector(sectorId));
    } else {
      dispatch(resetCells());
      setValue('cell', '');
    }
  }, [sectorId, dispatch, setValue]);

  useEffect(() => {
    if (cellId) {
      dispatch(fetchVillagesByCell(cellId));
    } else {
      dispatch(resetVillages());
      setValue('village', '');
    }
  }, [cellId, dispatch, setValue]);

  // Update votingVillageId when village changes
  useEffect(() => {
    if (villageId) {
      setValue('votingVillageId', villageId);
    }
  }, [villageId, setValue]);

  const handleLocationChange = (field: string, value: string) => {
    setValue(field as keyof NewVoterFormValues, value);

    // Reset dependent fields
    switch (field) {
      case 'province':
        setValue('district', '');
        setValue('sector', '');
        setValue('cell', '');
        setValue('village', '');
        break;
      case 'district':
        setValue('sector', '');
        setValue('cell', '');
        setValue('village', '');
        break;
      case 'sector':
        setValue('cell', '');
        setValue('village', '');
        break;
      case 'cell':
        setValue('village', '');
        break;
    }
  };

  const handleSubmit = (data: NewVoterFormValues) => {
    onSubmit(data);
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-start gap-4 rounded-md border border-[#e2e9f3] bg-white p-5 shadow-[0_16px_45px_rgba(26,53,91,0.06)] sm:items-center sm:p-7">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          className="h-11 shrink-0 rounded-xl px-3 text-[#2374cf] hover:bg-[#eaf3ff] sm:px-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">{t('TextBack')}</span>
        </Button>
        <div>
          <h2 className="text-xl font-semibold tracking-[-0.02em] text-[#172641] sm:text-2xl">
            {t('TextNewVoterRegistration')}
          </h2>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-[#71809a]">
            {t('TextNewVoterRegistrationDescription')}
          </p>
        </div>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <CardSection title={t('TextPersonalInformation')} icon={UserRound}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('TextFirstName')} <Asterisk />
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="!mt-1"
                      placeholder={t('PFirstName')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('TextLastName')} <Asterisk />
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="!mt-1"
                      placeholder={t('PLastName')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="genderId"
              render={({ fieldState }) => (
                <SelectComponent
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t('TextGender')}
                      <Asterisk />
                    </span>
                  }
                  options={genders?.map((gender) => ({
                    label: gender.enTranslation,
                    value: gender.id
                  }))}
                  onValueChange={(value) => setValue('genderId', value)}
                  value={watch('genderId')}
                  placeholder={t('PSelectGender')}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />

            <FormField
              control={form.control}
              name="fatherName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('TextFatherName')} <Asterisk />
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="!mt-1"
                      placeholder={t('PFatherName')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="motherName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('TextMotherName')} <Asterisk />
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="!mt-1"
                      placeholder={t('PMotherName')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dateOfBirth"
              render={({ field, fieldState }) => (
                <DatePicker
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t('TextDateOfBirth')}
                      <Asterisk />
                    </span>
                  }
                  selectedDate={field.value ? new Date(field.value) : undefined}
                  onDateChange={(date) => {
                    // Convert Date back to string format for the form
                    field.onChange(date ? date.toISOString().split('T')[0] : '');
                  }}
                  disabledDates={(date) => {
                    const today = new Date();
                    const sixteenYearsAgo = new Date(
                      today.getFullYear() - 16,
                      today.getMonth(),
                      today.getDate()
                    );
                    return date > sixteenYearsAgo;
                  }}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />

            <FormField
              control={form.control}
              name="placeOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('TextPlaceOfBirth')}</FormLabel>
                  <FormControl>
                    <Input
                      className="!mt-1"
                      placeholder={t('PPlaceOfBirth')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardSection>

        <CardSection
          title={t('TextContactInformation')}
          description={t('TextContactRequirement')}
          icon={Phone}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('TextEmail')}</FormLabel>
                  <FormControl>
                    <Input
                      className="!mt-1"
                      type="email"
                      placeholder={t('PEmail')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('TextPhoneNumber')}</FormLabel>
                  <FormControl>
                    <PhoneInput
                      className="!mt-1"
                      placeholder={t('PPhoneNumber')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t('TextAddress')}</FormLabel>
                  <FormControl>
                    <Input
                      className="!mt-1"
                      placeholder={t('PAddress')}
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardSection>

        <CardSection title={t('TextVotingLocation')} icon={MapPinned}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
            <FormField
              control={form.control}
              name="province"
              render={({ fieldState }) => (
                <SelectComponent
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t('TextProvince')}
                      <Asterisk />
                    </span>
                  }
                  options={provinces?.map((province) => ({
                    label: province.name,
                    value: province.id
                  }))}
                  onValueChange={(value) => handleLocationChange('province', value)}
                  value={provinceId}
                  placeholder={t('PSelectProvince')}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />

            <FormField
              control={form.control}
              name="district"
              render={({ fieldState }) => (
                <SelectComponent
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t('TextDistrict')}
                      <Asterisk />
                    </span>
                  }
                  options={districtsByProvince?.map((district) => ({
                    label: district.name,
                    value: district.id
                  }))}
                  onValueChange={(value) => handleLocationChange('district', value)}
                  value={districtId}
                  placeholder={t('PSelectDistrict')}
                  errorMessage={fieldState.error?.message}
                  disabled={!provinceId}
                />
              )}
            />

            <FormField
              control={form.control}
              name="sector"
              render={({ fieldState }) => (
                <SelectComponent
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t('TextSector')}
                      <Asterisk />
                    </span>
                  }
                  options={sectorsByDistrict?.map((sector) => ({
                    label: sector.name,
                    value: sector.id
                  }))}
                  onValueChange={(value) => handleLocationChange('sector', value)}
                  value={sectorId}
                  placeholder={t('PSelectSector')}
                  errorMessage={fieldState.error?.message}
                  disabled={!districtId}
                />
              )}
            />

            <FormField
              control={form.control}
              name="cell"
              render={({ fieldState }) => (
                <SelectComponent
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t('TextCell')}
                      <Asterisk />
                    </span>
                  }
                  options={cellsBySector?.map((cell) => ({
                    label: cell.name,
                    value: cell.id
                  }))}
                  onValueChange={(value) => handleLocationChange('cell', value)}
                  value={cellId}
                  placeholder={t('PSelectCell')}
                  errorMessage={fieldState.error?.message}
                  disabled={!sectorId}
                />
              )}
            />

            <FormField
              control={form.control}
              name="village"
              render={({ fieldState }) => (
                <SelectComponent
                  label={
                    <span className="inline-flex items-center gap-0.5">
                      {t('TextVillage')}
                      <Asterisk />
                    </span>
                  }
                  options={villagesByCell?.map((village) => ({
                    label: village.name,
                    value: village.id
                  }))}
                  onValueChange={(value) => handleLocationChange('village', value)}
                  value={villageId}
                  placeholder={t('PSelectVillage')}
                  errorMessage={fieldState.error?.message}
                  disabled={!cellId}
                />
              )}
            />
          </div>
        </CardSection>

        <CardSection title={t('TextOptionalInformation')} icon={FileBadge}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <FormField
                control={form.control}
                name="passportNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('TextPassportNumber')}</FormLabel>
                    <FormControl>
                      <Input
                        className="!mt-1"
                        placeholder={t('PPassportNumber')}
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="drivingLicenseNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('TextDrivingLicenseNumber')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="!mt-1"
                        placeholder={t('PDrivingLicenseNumber')}
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="otherIdType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('TextOtherIdType')}</FormLabel>
                    <FormControl>
                      <Input
                        className="!mt-1"
                        placeholder={t('POtherIdType')}
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="otherIdNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('TextOtherIdNumber')}</FormLabel>
                    <FormControl>
                      <Input
                        className="!mt-1"
                        placeholder={t('POtherIdNumber')}
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-6">
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">{t('TextReason')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('PReason')}
                        {...field}
                        disabled={isLoading}
                        rows={4}
                        className="!mt-1 resize-none rounded-sm border-[#dce5f1] focus-visible:ring-[#2474d2]/25 focus-visible:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </CardSection>

        <div className="flex flex-col gap-3 rounded-md border border-[#e2e9f3] bg-white p-5 shadow-[0_10px_30px_rgba(26,53,91,0.045)] sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="text-black bg-gray-100 hover:bg-gray-200 sm:order-1"
          >
            {t('TextCancel')}
          </Button>

          <Button
            type="submit"
            className="bg-[#338AF3] hover:bg-blue-800 sm:order-2"
            disabled={isLoading}
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('TextSubmitting')}
              </>
            ) : (
              t('TextSubmitRegistration')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
